import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', trek: '' });
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadBookings();
  }, [filters]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)));
      const { data } = await api.get(`/admin/bookings?${params}`);
      setBookings(data);
    } catch (err) {
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const headers = ['Booking ID', 'Customer', 'Trek', 'Start Date', 'End Date', 'Group Size', 'Total Cost', 'Amount Paid', 'Balance', 'Status', 'Created By', 'Created At'];
    const rows = bookings.map(b => {
      const payment = getPaymentSummary(b);
      return [
        b.id, b.customerName, b.trekName, b.startDate, b.endDate, b.groupSize,
        b.pricing.totalCost, payment.paid, payment.pending,
        b.paymentStatus, b.createdByName || b.createdBy, b.createdAt
      ];
    });
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bookings_export_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported!');
  };

  const deleteBooking = async (id, customerName) => {
    if (!window.confirm(`Are you sure you want to delete booking ${id} for ${customerName}? This cannot be undone.`)) return;
    try {
      await api.delete(`/bookings/${id}`);
      toast.success('Booking deleted');
      loadBookings();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete booking');
    }
  };

  const getPaymentSummary = (booking) => {
    const totalCost = booking.pricing?.totalCost || 0;

    if (booking.paymentStatus === 'fully_paid') {
      const paid = booking.pricing?.finalAmountReceived ?? totalCost;
      return { paid, pending: Math.max(0, totalCost - paid), label: 'Fully Paid' };
    }
    if (booking.paymentStatus === 'advance_paid') {
      const paid = booking.pricing?.advanceReceived ?? booking.pricing?.totalAdvance ?? 0;
      return { paid, pending: Math.max(0, totalCost - paid), label: 'Advance Paid' };
    }
    return { paid: 0, pending: totalCost, label: 'Pending' };
  };

  const startEditPaid = (booking) => {
    const payment = getPaymentSummary(booking);
    setEditingId(booking.id);
    setEditValue(String(payment.paid));
  };

  const cancelEditPaid = () => {
    setEditingId(null);
    setEditValue('');
  };

  const savePaidAmount = async (booking) => {
    const amount = Number(editValue);
    if (isNaN(amount) || amount < 0) return toast.error('Enter a valid amount');
    setSaving(true);
    try {
      const endpoint = booking.paymentStatus === 'fully_paid'
        ? `/bookings/${booking.id}/finalize-payment`
        : `/bookings/${booking.id}/advance-payment`;
      const body = booking.paymentStatus === 'fully_paid' ? { finalAmountReceived: amount } : { advanceReceived: amount };
      const { data } = await api.patch(endpoint, body);
      setBookings(prev => prev.map(b => b.id === data.id ? data : b));
      toast.success('Payment updated!');
      cancelEditPaid();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePaidInFull = async (booking) => {
    if (booking.paymentStatus === 'fully_paid') return;
    const totalCost = booking.pricing?.totalCost || 0;
    const input = window.prompt(`Enter the final amount paid by ${booking.customerName}:`, String(totalCost));
    if (input === null) return;
    const amount = Number(input);
    if (isNaN(amount) || amount < 0) return toast.error('Enter a valid amount');
    try {
      const { data } = await api.patch(`/bookings/${booking.id}/finalize-payment`, { finalAmountReceived: amount });
      setBookings(prev => prev.map(b => b.id === data.id ? data : b));
      toast.success('Marked as fully paid!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to finalize payment');
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">📚 All Bookings</h1>
        <button onClick={exportCSV} className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-green-700">
          📥 Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <select value={filters.status} onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="advance_paid">Advance Paid</option>
            <option value="fully_paid">Fully Paid</option>
          </select>
          <input type="text" placeholder="Filter by trek name..." value={filters.trek}
            onChange={e => setFilters(f => ({ ...f, trek: e.target.value }))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
          <button onClick={() => setFilters({ status: '', trek: '' })}
            className="px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Clear Filters</button>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Booking ID</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Customer</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Trek</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Dates</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Group</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Paid</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Balance</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Status</th>
                <th className="text-center px-3 py-3 font-medium text-gray-600">Fully Paid?</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Agent</th>
                <th className="text-left px-3 py-3 font-medium text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={11} className="text-center py-8 text-gray-400">Loading...</td></tr>
              ) : bookings.length === 0 ? (
                <tr><td colSpan={11} className="text-center py-8 text-gray-400">No bookings found</td></tr>
              ) : (
                bookings.map(b => {
                  const payment = getPaymentSummary(b);
                  const isEditing = editingId === b.id;
                  return (
                  <tr key={b.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 text-xs font-mono text-primary-600">{b.id}</td>
                    <td className="px-3 py-3 font-medium">{b.customerName}</td>
                    <td className="px-3 py-3 text-gray-600">{b.trekName}</td>
                    <td className="px-3 py-3 text-xs">{b.startDate} to {b.endDate}</td>
                    <td className="px-3 py-3">{b.groupSize}</td>
                    <td className="px-3 py-3 text-green-600">
                      {isEditing ? (
                        <div className="flex items-center gap-1">
                          <input type="number" min="0" autoFocus value={editValue} onChange={e => setEditValue(e.target.value)}
                            className="w-24 px-1.5 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-primary-500 outline-none" />
                          <button onClick={() => savePaidAmount(b)} disabled={saving} title="Save" className="text-green-600 hover:text-green-800 disabled:opacity-50">✓</button>
                          <button onClick={cancelEditPaid} title="Cancel" className="text-gray-400 hover:text-gray-600">✗</button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          ₹{payment.paid.toLocaleString('en-IN')}
                          <button onClick={() => startEditPaid(b)} title="Edit amount paid" className="text-gray-400 hover:text-blue-600 text-xs">✏️</button>
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-red-600">₹{payment.pending.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${
                        b.paymentStatus === 'fully_paid' ? 'bg-green-100 text-green-700' :
                        b.paymentStatus === 'advance_paid' ? 'bg-blue-100 text-blue-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {payment.label}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-center">
                      <input type="checkbox" checked={b.paymentStatus === 'fully_paid'} disabled={b.paymentStatus === 'fully_paid'}
                        onChange={() => handleTogglePaidInFull(b)} title={b.paymentStatus === 'fully_paid' ? 'Already marked fully paid' : 'Mark as fully paid'}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer disabled:cursor-not-allowed" />
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500">{b.createdByName || b.createdBy}</td>
                    <td className="px-3 py-3">
                      <div className="flex gap-2">
                        <button onClick={() => navigate(`/admin/edit-booking/${b.id}`)}
                          className="text-blue-500 hover:text-blue-700 text-xs font-medium hover:underline">
                          ✏️ Edit
                        </button>
                        <button onClick={() => deleteBooking(b.id, b.customerName)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium hover:underline">
                          🗑️ Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                )})
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t bg-gray-50 text-sm text-gray-500">
          Total: {bookings.length} bookings
        </div>
      </div>
    </div>
  );
}
